/**
 * Authentication Store for Phishiness Frontend
 *
 * Supports both session-based authentication (BFF pattern) and JWT tokens.
 * Session-based auth is preferred - cookies are managed by the browser,
 * so we only track authentication state, not tokens.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, UserRole } from '@/types/user';

/** Authentication source type */
export type AuthSource = 'password' | 'sso_saml' | 'sso_oidc' | 'passkey';

/** Session information from the BFF */
export interface SessionInfo {
  session_id: string;
  user_id: string;
  company_id: string;
  device_type: string;
  browser: string;
  os: string;
  ip_address: string;
  created_at: string;
  last_accessed_at: string;
  is_current: boolean;
}

/** MFA status for the current user */
export interface MfaStatus {
  enabled: boolean;
  methods: string[];
  enforced: boolean;
}

interface AuthState {
  // User state
  user: User | null;
  company_id: string | null;
  isAuthenticated: boolean;

  // Session state (BFF pattern)
  sessionId: string | null;
  mfaVerified: boolean;
  mfaRequired: boolean;
  authSource: AuthSource | null;

  // MFA status
  mfaStatus: MfaStatus | null;

  // Legacy JWT support (for migration period)
  accessToken: string | null;
  refreshToken: string | null;

  // UI state
  isLoading: boolean;
  error: string | null;
  lastActivity: number | null;

  // Actions
  setUser: (user: User | null) => void;
  setSession: (sessionId: string, mfaVerified?: boolean) => void;
  setMfaRequired: (required: boolean) => void;
  setMfaVerified: (verified: boolean) => void;
  setMfaStatus: (status: MfaStatus | null) => void;
  setAuthSource: (source: AuthSource | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  updateLastActivity: () => void;

  // Computed helpers
  hasRole: (role: UserRole) => boolean;
  canAccess: (requiredRole: UserRole) => boolean;
}

/** Role hierarchy for permission checks */
const ROLE_HIERARCHY: Record<UserRole, number> = {
  platform_admin: 4,
  admin: 3,
  analyst: 2,
  viewer: 1,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      company_id: null,
      isAuthenticated: false,
      sessionId: null,
      mfaVerified: false,
      mfaRequired: false,
      authSource: null,
      mfaStatus: null,
      accessToken: null,
      refreshToken: null,
      isLoading: false,
      error: null,
      lastActivity: null,

      // Set user and derive authentication state
      setUser: (user) =>
        set({
          user,
          company_id: user?.company_id ?? null,
          isAuthenticated: !!user,
        }),

      // Set session ID (BFF pattern - cookie is httpOnly, we just track state)
      setSession: (sessionId, mfaVerified = false) =>
        set({
          sessionId,
          mfaVerified,
          isAuthenticated: true,
          lastActivity: Date.now(),
        }),

      // MFA flow control
      setMfaRequired: (required) => set({ mfaRequired: required }),
      setMfaVerified: (verified) => set({ mfaVerified: verified }),
      setMfaStatus: (status) => set({ mfaStatus: status }),

      // Set authentication source
      setAuthSource: (source) => set({ authSource: source }),

      // Legacy JWT support
      setTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken,
          isAuthenticated: true,
          lastActivity: Date.now(),
        }),

      // Clear all auth state (logout)
      clearAuth: () =>
        set({
          user: null,
          company_id: null,
          isAuthenticated: false,
          sessionId: null,
          mfaVerified: false,
          mfaRequired: false,
          authSource: null,
          mfaStatus: null,
          accessToken: null,
          refreshToken: null,
          error: null,
          lastActivity: null,
        }),

      // UI state management
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      updateLastActivity: () => set({ lastActivity: Date.now() }),

      // Check if user has specific role
      hasRole: (role) => {
        const { user } = get();
        return user?.role === role;
      },

      // Check if user can access resource requiring specific role
      canAccess: (requiredRole) => {
        const { user } = get();
        if (!user) return false;
        return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[requiredRole];
      },
    }),
    {
      name: 'phishiness-auth',
      // Only persist minimal state - session is in httpOnly cookie
      partialize: (state) => ({
        // Don't persist tokens for security
        // User will be re-fetched on page load via /auth/me endpoint
        lastActivity: state.lastActivity,
        // Keep these for backwards compatibility during migration
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

/**
 * Hook for session-based authentication check
 * Use this to check if the user needs to re-authenticate
 */
export function useSessionValid(): boolean {
  const { isAuthenticated, lastActivity } = useAuthStore();

  if (!isAuthenticated || !lastActivity) {
    return false;
  }

  // Consider session stale after 30 minutes of no activity
  const SESSION_STALE_MS = 30 * 60 * 1000;
  const isStale = Date.now() - lastActivity > SESSION_STALE_MS;

  return !isStale;
}

/**
 * Hook for MFA requirement check
 * Returns true if MFA is required but not yet verified
 */
export function useMfaPending(): boolean {
  const { mfaRequired, mfaVerified } = useAuthStore();
  return mfaRequired && !mfaVerified;
}
